// I/O schemas for the M2 i18n admin surface (motiflink §1.5).

import { z } from 'zod';

const languageCode = z
  .string()
  .min(2)
  .max(10)
  .regex(/^[a-z][a-z0-9-]*$/, 'lowercase letters, digits and dashes only');

const direction = z.enum(['ltr', 'rtl']);

export const createLanguageInput = z.object({
  code: languageCode,
  name: z.string().min(1).max(80),
  nativeName: z.string().min(1).max(80),
  direction: direction.default('ltr'),
  isActive: z.boolean().default(true),
  sortOrder: z.number().int().min(0).max(9999).default(0),
});

// `code` is intentionally absent: it is the FK target of translations.language_code
// and immutable once created (motiflink §1.5).
export const updateLanguageInput = z
  .object({
    name: z.string().min(1).max(80),
    nativeName: z.string().min(1).max(80),
    direction,
    isActive: z.boolean(),
    sortOrder: z.number().int().min(0).max(9999),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'no fields to update' });

export const reorderLanguagesInput = z.object({
  order: z
    .array(z.object({ id: z.string().uuid(), sortOrder: z.number().int().min(0).max(9999) }))
    .min(1)
    .max(100),
});

export const translationKeyString = z
  .string()
  .min(1)
  .max(240)
  .regex(/^[A-Za-z0-9_]+(\.[A-Za-z0-9_]+)*$/, 'dot-separated key segments');

export const upsertTranslationInput = z.object({
  translationKey: translationKeyString,
  languageCode,
  translationValue: z.string().max(8000),
  category: z.string().max(80).nullish(),
});

export const updateTranslationInput = z
  .object({
    translationValue: z.string().max(8000),
    category: z.string().max(80).nullable(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'no fields to update' });

export const importTranslationsInput = z.object({
  rows: z.array(upsertTranslationInput).min(1).max(5000),
});

export const listTranslationsQuery = z.object({
  lang: languageCode.optional(),
  category: z.string().max(80).optional(),
  q: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

export const exportTranslationsQuery = z.object({ lang: languageCode.optional() });

export type CreateLanguageInput = z.infer<typeof createLanguageInput>;
export type UpdateLanguageInput = z.infer<typeof updateLanguageInput>;
export type UpsertTranslationInput = z.infer<typeof upsertTranslationInput>;
export type ListTranslationsQuery = z.infer<typeof listTranslationsQuery>;
